import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  Stack,
  Button,
  Alert,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { ClientLayout } from "../../components/layout/ClientLayout";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import ChatWindow from "../../components/ChatWindow";
import DocumentUploader from "../../components/DocumentUploader";
import DocumentsList from "../../components/DocumentsList";
import { useChat } from "../../hooks/useChat";
import { useDocuments } from "../../hooks/useDocuments";
import { PaymentModal } from "../../components/PaymentModal";
import { ClientPlanReview } from "../../components/planning/ClientPlanReview";
import { UpdatesList } from "../../components/updates/UpdatesList";
import { useUpdates } from "../../hooks/useUpdates";
import { Milestone } from "../../components/milestonesTypes";

interface ProjectDetailData {
  id: string;
  title: string;
  address: string;
  budget: number;
  start_date: string;
  estimated_completion_date: string;
  status: "planning" | "in_progress" | "on_hold" | "complete";
  contractor_id: string;
  contractor?: {
    name: string | null;
    email: string;
  } | null;
}

interface PaymentTarget {
  milestoneId: string;
  milestoneTitle: string;
  amountCents: number;
}

const statusLabels: Record<string, string> = {
  planning: "Planning",
  in_progress: "In Progress",
  on_hold: "On Hold",
  complete: "Complete",
};

const statusColors: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success"
> = {
  planning: "default",
  in_progress: "primary",
  on_hold: "warning",
  complete: "success",
};

/**
 * Example Client Project Detail Page with Chat Integration
 * This demonstrates how to add chat to a client-side project detail view
 */
export function ClientProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { profile: user } = useAuth();

  const [tab, setTab] = useState(0);
  const [project, setProject] = useState<ProjectDetailData | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<PaymentTarget | null>(null);

  const { updates, loading: updatesLoading, error: updatesError } = useUpdates(projectId || "");

  // Initialize chat hook
  const chat = useChat(
    projectId || "",
    user?.id || "",
    user?.name || "",
    "client",
  );

  // Initialize documents hook
  const documents = useDocuments(projectId || "");

  // Load project data
  React.useEffect(() => {
    const loadProject = async () => {
      if (!projectId) return;

      setLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from("projects")
          .select(
            `
            id,
            title,
            address,
            budget,
            start_date,
            estimated_completion_date,
            status,
            contractor_id,
            contractor:users!contractor_id(name, email)
          `,
          )
          .eq("id", projectId)
          .single();

        if (fetchError) throw fetchError;
        setProject(data as unknown as ProjectDetailData);

        const { data: milestoneData } = await supabase
          .from("milestones")
          .select(
            "id, project_id, title, target_date, completion_date, status, milestone_order, percentage_of_project",
          )
          .eq("project_id", projectId)
          .order("milestone_order", { ascending: true });

        setMilestones((milestoneData ?? []) as Milestone[]);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load project";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  if (loading) {
    return (
      <ClientLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </ClientLayout>
    );
  }

  if (error || !project) {
    return (
      <ClientLayout>
        <Box sx={{ p: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/client")}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Alert severity="error">{error || "Project not found"}</Alert>
        </Box>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/client")}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {project.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {project.address || "No address on file"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={statusLabels[project.status]}
            color={statusColors[project.status]}
            variant="outlined"
          />
        </Box>

        <Card>
          <CardContent>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab label="Project Details" />
              <Tab label="Plan" />
              <Tab label="Updates" />
              <Tab label="Milestones & Payments" />
              <Tab label="Chat" />
              <Tab label="Documents" />
            </Tabs>

            <Box sx={{ py: 3 }}>
              {/* Project Details Tab */}
              {tab === 0 && (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Project Information
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contractor
                      </Typography>
                      <Typography variant="body2">
                        {project.contractor?.name || "Unknown"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body2">
                        {project.start_date || "Not set"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Target Completion
                      </Typography>
                      <Typography variant="body2">
                        {project.estimated_completion_date || "Not set"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Budget
                      </Typography>
                      <Typography variant="body2">
                        {project.budget
                          ? `$${project.budget.toLocaleString()}`
                          : "Not set"}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              )}

              {/* Plan Review Tab */}
              {tab === 1 && projectId && (
                <ClientPlanReview projectId={projectId} />
              )}

              {/* Updates Tab */}
              {tab === 2 && (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Progress Updates
                  </Typography>
                  <UpdatesList
                    updates={updates}
                    loading={updatesLoading}
                    error={updatesError}
                    canDelete={false}
                  />
                </Stack>
              )}

              {/* Milestones & Payments Tab */}
              {tab === 3 && (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Milestones
                  </Typography>
                  {milestones.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No milestones yet.
                    </Typography>
                  ) : (
                    milestones.map((milestone) => {
                      const amountCents = project.budget
                        ? Math.round(
                            (project.budget *
                              milestone.percentage_of_project) /
                              100 *
                              100,
                          )
                        : 0;
                      return (
                        <Box
                          key={milestone.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {milestone.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {milestone.percentage_of_project}% of project
                              {amountCents > 0 &&
                                ` · $${(amountCents / 100).toLocaleString()}`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={milestone.status.replace("_", " ")}
                              size="small"
                              color={
                                milestone.status === "complete"
                                  ? "success"
                                  : milestone.status === "in_progress"
                                    ? "primary"
                                    : "default"
                              }
                              variant="outlined"
                            />
                            {milestone.status === "complete" && amountCents >= 50 && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  setPaymentTarget({
                                    milestoneId: milestone.id,
                                    milestoneTitle: milestone.title,
                                    amountCents,
                                  })
                                }
                              >
                                Pay
                              </Button>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              )}

              {/* Chat Tab */}
              {tab === 4 && (
                <Box
                  sx={{
                    height: "600px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ChatWindow
                    projectId={projectId || ""}
                    currentUserId={user?.id || ""}
                    currentUserName={user?.name || ""}
                    messages={chat.messages}
                    typingUsers={chat.typingUsers}
                    loading={chat.loading}
                    initialLoading={chat.loading && chat.messages.length === 0}
                    error={chat.error}
                    hasMore={chat.hasMore}
                    onSendMessage={chat.sendMessage}
                    onLoadMore={chat.loadMoreMessages}
                    disabled={false}
                  />
                </Box>
              )}

              {/* Documents Tab */}
              {tab === 5 && (
                <Stack spacing={3}>
                  <DocumentUploader
                    onUpload={(file, category) =>
                      documents.uploadDocument(file, category, user?.id || "")
                    }
                  />
                  <DocumentsList
                    documents={documents.documents}
                    loading={documents.loading}
                    error={documents.error}
                    currentUserId={user?.id || ""}
                    onDelete={documents.deleteDocument}
                  />
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {paymentTarget && (
        <PaymentModal
          open={!!paymentTarget}
          projectId={project.id}
          milestoneId={paymentTarget.milestoneId}
          amountCents={paymentTarget.amountCents}
          description={`Payment for milestone: ${paymentTarget.milestoneTitle}`}
          onClose={() => setPaymentTarget(null)}
          onSuccess={() => {
            setPaymentTarget(null);
          }}
        />
      )}
    </ClientLayout>
  );
}

export default ClientProjectDetail;
